import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { type FieldTypeWithOnlyValues } from '@databases/field-value';
import { messages } from '@databases/intl/messages';
import { logError } from '@databases/logs';
import { useIsMounted } from '@databases/react';
import { defaultStoreUtils, type DefaultRootState } from '@databases/redux';
import { getService } from '@databases/service-container';
import { currentSnackbarState } from '@databases/snackbar/snackbar';
import {
  type JiraIssueRef,
  type ResolvedJiraIssueRef
} from '@databases/types/confluence';
import { type DatabaseFieldType } from '@databases/types/database';
import { getReferencedJiraIssueDetailsField } from '@databases/utils/database/field-types/jiraIssueDetails';
import {
  getDatabaseEntryRequired,
  getConvertedDatabaseValue
} from '@databases/utils/database/utils';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { loadJiraIssue } from '@databases/utils/redux/jira/actions';
import { selectJiraIssueOptional } from '@databases/utils/redux/jira/selectors';
import { useCallback, useState } from 'react';

import { type EditComponentProps } from '../types.ts';

import { updateIssueAssignee } from './api.ts';

const selectJiraAssigneeData = (
  state: DefaultRootState,
  {
    contentId,
    entryId,
    field
  }: {
    contentId: string;
    entryId: string | undefined;
    field: EditComponentProps<DatabaseFieldType.JIRA_ISSUE_DETAILS>['field'];
  }
) => {
  const database = selectDatabaseByContentIdRequired(state, { contentId });
  const entry = entryId ? getDatabaseEntryRequired(database, entryId) : undefined;

  // Get referenced field and value directly without full database
  const referencedField = getReferencedJiraIssueDetailsField({ database, field });
  const fieldValue =
    entry && referencedField
      ? getConvertedDatabaseValue(database, referencedField, entry, state)
      : undefined;

  // Extract issue refs from field value directly
  const issueRefs: JiraIssueRef[] =
    fieldValue?.value?.flatMap((v: (object | { key: string }) & { _id: string }) =>
      v && 'key' in v && v.key
        ? [
            {
              cloudId: referencedField?.settings?.cloudId || '',
              serverId: referencedField?.settings?.jiraServerId || '',
              key: v.key
            }
          ]
        : []
    ) ?? [];

  // Get resolved issues only for the specific refs we need
  const resolvedIssues = issueRefs.map((ref) =>
    ref ? selectJiraIssueOptional(state, ref) : undefined
  );

  // Extract assignee items from resolved issues
  // Keep the same length as issueRefs and resolvedIssues to maintain proper indexing
  const assigneeItems = resolvedIssues.map((issue) => {
    const assigneeColumn = issue?.columns?.assignee;
    if (assigneeColumn && assigneeColumn.data) {
      const values = Array.isArray(assigneeColumn.data)
        ? assigneeColumn.data
        : [assigneeColumn.data];
      return {
        type: assigneeColumn.type,
        text: assigneeColumn.text,
        values
      } as FieldTypeWithOnlyValues;
    }
    return undefined;
  });

  return {
    issueRefs,
    assigneeItems,
    resolvedIssues
  };
};

export interface JiraAssigneeData {
  issueRefs: JiraIssueRef[];
  assigneeItems: (FieldTypeWithOnlyValues | undefined)[];
  resolvedIssues: (ResolvedJiraIssueRef | undefined)[];
  updateAssignee: (issueRef: JiraIssueRef, assigneeAccountId: string) => Promise<void>;
  isUpdating: (issueRef: JiraIssueRef) => boolean;
}

export function useJiraAssigneeData(
  contentId: string,
  entryId: string | undefined,
  field: EditComponentProps<DatabaseFieldType.JIRA_ISSUE_DETAILS>['field']
): JiraAssigneeData {
  const [updatingIssues, setUpdatingIssues] = useState<Set<string>>(new Set());
  const isMountedRef = useIsMounted();
  const dispatch = defaultStoreUtils.useDispatch();
  const analytics = useAnalytics();

  const updateAssignee = useCallback(
    async (issueRef: JiraIssueRef, assigneeAccountId: string) => {
      if (!issueRef?.ari) {
        return;
      }

      const issueKey = issueRef.key;
      setUpdatingIssues((prev) => new Set(prev).add(issueKey));

      // Note: We use generic values for analytics since getting current state in callback is complex

      try {
        await updateIssueAssignee({
          assigneeAccountId,
          issueAri: issueRef.ari
        });

        // Send analytics event for successful assignee update
        analytics.sendJiraFieldUpdatedEvent(
          {
            contentId,
            issueKey: issueRef.key,
            fieldType: 'assignee',
            databaseContainer: getDatabaseContainerType()
          },
          'jiraAssignee',
          'jiraAssigneeValueEdit'
        );

        const resolvedIssue = await dispatch(
          loadJiraIssue(issueRef, { columns: ['assignee'], forceReload: true })
        );
        if (resolvedIssue?.url) {
          const smartLinkClient = getService('SmartLinkClient');
          smartLinkClient.forceRefreshData(resolvedIssue.url);
        }
      } catch (e) {
        const error =
          e instanceof Error
            ? e
            : new Error(`jira assignee update failed for issue ${issueRef.key}`);

        logError('jira-assignee/update:failed', error);
        currentSnackbarState.setMessage(
          messages['jira-assignee.update-failed.non-final'].defaultMessage,
          {
            id: messages['jira-assignee.update-failed.non-final'].id,
            type: 'error',
            showCloseButton: true,
            hideAfter: 4000
          }
        );

        throw error;
      } finally {
        if (isMountedRef.current) {
          setUpdatingIssues((prev) => {
            const next = new Set(prev);
            next.delete(issueKey);
            return next;
          });
        }
      }
    },
    [dispatch, isMountedRef, contentId, analytics]
  );

  const data = defaultStoreUtils.useEqualSelector((state) =>
    selectJiraAssigneeData(state, { contentId, entryId, field })
  );

  const isUpdating = useCallback(
    (issueRef: JiraIssueRef) => {
      return updatingIssues.has(issueRef.key);
    },
    [updatingIssues]
  );

  return {
    ...data,
    updateAssignee,
    isUpdating
  };
}
